import React from "react";
import ExperimentalOverviewSection from "../components/dashboard/experimentalOverViewSection";
import FunctionalGroupsSection from "../components/dashboard/functionalGroupsSection";
import GeneralOverviewSection from "../components/dashboard/generalOverViewSection";
import LipidTypesSection from "../components/dashboard/lipidTypeSection";
import Navbar from "../components/ui/navbar";
import useDashboardData from "../hooks/useDashboardData";
import { useUmapData } from "../hooks/useUmapData";

interface DashboardPageProps {
  experimentId?: string;
}

const Dashboard: React.FC<DashboardPageProps> = ({ experimentId }) => {
  const { dashboardData, loading, error } = useDashboardData(experimentId);
  const { lnpData, experimentData } = useUmapData();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto border-t-4 border-b-4 border-blue-500 rounded-full animate-spin"></div>
          <p className="mt-4 text-lg text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg">
            <p>ERROR:</p>
            <p className="font-medium">{error}</p>
          </div>
          <button
            className="px-4 py-2 mt-4 text-white bg-blue-500 rounded hover:bg-blue-600"
            onClick={() => window.location.reload()}
          >
            Retrying...
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-lg text-gray-600">No Data Available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container p-2 mx-auto">
        <GeneralOverviewSection
          data={dashboardData.generalOverview}
          lnpData={lnpData}
        />

        <LipidTypesSection
          helperLipids={dashboardData.lipidTypes.helperLipids}
          pegLipids={dashboardData.lipidTypes.pegLipids}
          experimentData={experimentData}
        />

        <FunctionalGroupsSection
          functionalGroups={dashboardData.functionalGroups.functionalGroups}
          nitrogenDistribution={
            dashboardData.functionalGroups.nitrogenDistribution
          }
          saturationData={dashboardData.functionalGroups.saturationData}
          tailLengthData={dashboardData.functionalGroups.tailLengthData}
        />

        <ExperimentalOverviewSection
          cargoData={dashboardData.experimentalOverview.cargoData}
          cargoTypeData={dashboardData.experimentalOverview.cargoTypeData}
          methodData={dashboardData.experimentalOverview.methodData}
          routeData={dashboardData.experimentalOverview.routeData}
          targetData={dashboardData.experimentalOverview.targetData}
          typeData={dashboardData.experimentalOverview.typeData}
          batchingData={dashboardData.experimentalOverview.batchingData}
          mixingData={dashboardData.experimentalOverview.mixingData}
          boxPlotData={dashboardData.experimentalOverview.boxPlotData}
        />
      </div>
    </div>
  );
};

const DashboardPage: React.FC = () => {
  return (
    <>
      <div className="relative flex-shrink-0 w-full py-8 border-b border-1">
        <Navbar />
      </div>
      <div className="flex flex-col">
        <div className="w-full">
          <Dashboard />
        </div>
      </div>
    </>
  );
};

export default DashboardPage;
